/* eslint-disable i18next/no-literal-string */
import { useEffect, useRef, useState } from "react";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

/**
 * Ported from bouncieweb's `components/RainDropEffect.js` (originally loaded via
 * `next/dynamic(..., { ssr: false })`).
 *
 * A WebGL raindrops-on-glass effect (raindrop-fx) rendered behind the Weather
 * Alerts item, with a static image fallback for when WebGL fails. In the
 * original, `next/dynamic({ ssr:false })` kept it client-only; here the plain
 * <canvas>/<img> render harmlessly on the server and all WebGL work is confined
 * to a client-only useEffect (per porting rule 10/12), so nothing touches the
 * canvas/WebGL during SSR.
 *
 * The background/static textures are loaded by public URL rather than bundled
 * imports, since raindrop-fx wants a plain image URL for its `background`.
 *
 * WebGL health check: after start, we sample a pixel; if it's pure black/white
 * (a known post-navigation breakage) we fade the canvas out and show the static
 * fallback instead — reproduced from the original verbatim.
 */

// bgImage feeds raindrop-fx's `background` option, which wants a single concrete
// URL (no format-negotiation mechanism) — stays a plain JPEG.
const bgImage = imageUrl("/landing/texture-drizzle-bg.jpg");
const staticImage = imageUrl("/landing/raindrops-static.jpg");

export default function RainDropEffect() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isWorking, setIsWorking] = useState(false);

  // The <canvas>/<img> render harmlessly on the server; all WebGL/raindrop-fx
  // work is confined to this client-only effect, so there is no SSR access to
  // window/canvas contexts during render (replaces the original next/dynamic
  // ssr:false wrapper).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // raindrop-fx touches WebGL/DOM globals at module-eval time, so a static
    // top-level import crashes SSR. Load it via a client-only dynamic import
    // inside this effect — the true equivalent of the original's
    // `next/dynamic(..., { ssr: false })` wrapper.
    let raindropFx: { resize: (w: number, h: number) => void; start: () => void; stop: () => void } | null =
      null;
    let cancelled = false;
    let healthCheck: ReturnType<typeof setTimeout> | undefined;

    const handleResize = () => {
      if (!raindropFx) return;
      const rect2 = canvas.getBoundingClientRect();
      raindropFx.resize(rect2.width, rect2.height);
    };

    void import("raindrop-fx")
      .then(({ default: RaindropFX }) => {
        if (cancelled || !canvasRef.current) return;

        raindropFx = new RaindropFX({
          canvas,
          background: bgImage,
          spawnSize: [30, 80],
          slipRate: 0,
          gravity: 1000,
        });

        window.addEventListener("resize", handleResize);
        raindropFx.start();

        /*
         * If you route away from this page and back, the webgl rendering breaks in
         * a way that is difficult to prevent. This at least detects the error by
         * checking whether a certain pixel is completely black or white, which never
         * happens when the canvas renders correctly, so we can fall back to a still
         * image.
         */
        healthCheck = setTimeout(() => {
          const gl =
            (canvas.getContext("webgl") as WebGLRenderingContext | null) ||
            (canvas.getContext("webgl2") as WebGL2RenderingContext | null);
          if (!gl) return;
          const pixel = new Uint8Array(4);
          requestAnimationFrame(() => {
            gl.readPixels(200, 10, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
            setIsWorking(
              pixel[0] !== 0 &&
                pixel[0] !== 255 &&
                pixel[1] !== 0 &&
                pixel[1] !== 255 &&
                pixel[2] !== 0 &&
                pixel[2] !== 255,
            );
          });
        }, 1000);
      })
      .catch(() => {
        // WebGL / raindrop-fx unavailable — keep the static image fallback.
      });

    return () => {
      cancelled = true;
      if (healthCheck) clearTimeout(healthCheck);
      window.removeEventListener("resize", handleResize);
      // raindrop-fx exposes stop() to halt the render loop.
      raindropFx?.stop();
    };
  }, []);

  return (
    <>
      {/* FallbackImage */}
      <Picture
        src={staticImage}
        alt=""
        className="absolute top-1/2 left-1/2 max-h-full max-w-full -translate-x-1/2 -translate-y-1/2 object-cover"
      />
      {/* RainDropCanvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-1/2 left-1/2 h-[130%] w-[110%] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-1000 ease-linear"
        style={{ opacity: isWorking ? 1 : 0 }}
      />
    </>
  );
}
